import { useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";

const storageKey = (pathname: string) => `smartBack:${pathname}`;

// Back that returns to the page this one was opened from (passed as
// `location.state.from`), not through every history entry made since.
// The origin is kept in sessionStorage per page because in-page
// `setSearchParams(..., { replace: true })` wipes location.state. It is only
// reused on POP (refresh / browser back-forward); a fresh arrival with no
// `from` clears it so a stale origin from an earlier visit never leaks in.
export const useSmartBack = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const [origin] = useState<string>(() => {
    const key = storageKey(location.pathname);
    const current = `${location.pathname}${location.search}`;
    const from = (location.state as { from?: string } | null)?.from;
    try {
      if (from && from !== current) {
        sessionStorage.setItem(key, from);
        return from;
      }
      if (navigationType === "POP") return sessionStorage.getItem(key) || "";
      sessionStorage.removeItem(key);
    } catch {
      return from && from !== current ? from : "";
    }
    return "";
  });

  return () => {
    if (origin) navigate(origin);
    else navigate(-1);
  };
};
